import BaseModal from "../common/HPBaseModal";
import { FileDownloadIcon, WarningIcon } from "@assets/images";
import { ModalProps } from "@_constants/modal";
import { t } from "@utils/i18n";
import { useAppDispatch } from "@/store";
import { modalDispatcher } from "@/store/slices/modal-slice";

interface ImportModalProps extends ModalProps {
  surveysCount: number;
  confirmAction: () => void;
  availableSurveys?: Array<{ surveyId: string; name: string }>;
  unavailableSurveys?: Array<{
    surveyId: string;
    name: string;
    importState: string;
  }>;
}

const ImportSelectedSurveysModal: React.FC<ImportModalProps> = ({
  surveysCount,
  confirmAction,
  availableSurveys = [],
  unavailableSurveys = [],
}) => {
  const dispatch = useAppDispatch();
  const { closeTopModal } = modalDispatcher(dispatch);

  const hasUnavailableSurveys = unavailableSurveys.length > 0;
  const hasAvailableSurveys = availableSurveys.length > 0;
  const allSurveysUnavailable = hasUnavailableSurveys && !hasAvailableSurveys;

  // If all surveys are unavailable, show disabled modal with warning
  if (allSurveysUnavailable) {
    return (
      <BaseModal
        onCancel={closeTopModal}
        title={t("modals.importSurveys.title")}
        confirmButtonText={t("modals.importSurveys.confirmButton")}
        cancelButtonText={t("modals.importSurveys.cancelButton")}
        confirmButtonIcon={<FileDownloadIcon />}
        loading={true} // This will disable the confirm button
      >
        <div className="space-y-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <WarningIcon className="w-4 h-4" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  {t("modals.importSurveys.noAvailableTitle") ||
                    "No surveys available for import"}
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    {t("modals.importSurveys.noAvailableMessage") ||
                      "All selected surveys are already imported or in progress."}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <p className="font-medium text-gray-700 mb-2">
              {t("modals.importSurveys.unavailableTitle") ||
                "The following surveys are already imported or in progress:"}
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 max-h-32 overflow-y-auto">
              {unavailableSurveys.map((survey) => (
                <li key={survey.surveyId}>
                  {survey.name} (
                  {survey.importState === "IMPORTED"
                    ? "Already imported"
                    : "In progress"}
                  )
                </li>
              ))}
            </ul>
          </div>
        </div>
      </BaseModal>
    );
  }

  // If there are unavailable surveys, show detailed information
  if (hasUnavailableSurveys) {
    return (
      <BaseModal
        onConfirm={confirmAction}
        onCancel={closeTopModal}
        title={t("modals.importSurveys.title")}
        confirmButtonText={
          t("modals.importSurveys.importRestButton") ||
          "Import the rest of surveys"
        }
        cancelButtonText={t("modals.importSurveys.cancelButton")}
        confirmButtonIcon={<FileDownloadIcon />}
      >
        <div className="space-y-4">
          <p>
            {t("modals.importSurveys.partialMessage", {
              available: availableSurveys.length,
              total: surveysCount,
            }) ||
              `${availableSurveys.length} out of ${surveysCount} surveys can be imported.`}
          </p>

          <div>
            <p className="font-medium text-gray-700 mb-2">
              {t("modals.importSurveys.unavailableTitle") ||
                "The following surveys are already imported or in progress:"}
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 max-h-32 overflow-y-auto">
              {unavailableSurveys.map((survey) => (
                <li key={survey.surveyId}>
                  {survey.name} (
                  {survey.importState === "IMPORTED"
                    ? "Already imported"
                    : "In progress"}
                  )
                </li>
              ))}
            </ul>
          </div>
        </div>
      </BaseModal>
    );
  }

  // Default case - all surveys are available
  return (
    <BaseModal
      onConfirm={confirmAction}
      onCancel={closeTopModal}
      title={t("modals.importSurveys.title")}
      message={t("modals.importSurveys.message", { count: surveysCount })}
      confirmButtonText={t("modals.importSurveys.confirmButton")}
      cancelButtonText={t("modals.importSurveys.cancelButton")}
      confirmButtonIcon={<FileDownloadIcon />}
    />
  );
};

export default ImportSelectedSurveysModal;
